import { useEffect, useMemo, useState } from "react";
import { useClaudeStream } from "../hooks/useClaudeStream";

/** Petits helpers */
const MODELS = [
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { id: "claude-opus-4-7", label: "Claude Opus 4.7" },
];

const LS_KEY = "claude_stream_history_v1";
const MAX_HISTORY = 8;

/** conversion chars → tokens approx pour live cost */
const charsToTokens = (c) => Math.max(1, Math.round(c / 4));

export default function ClaudeTester() {
  const LS_OPEN = "claude_stream_open";
  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) !== "0");
  const [msg, setMsg] = useState("Explique l’Amazonie en 3 points.");
  const [model, setModel] = useState(MODELS[0].id);
  const [maxTokens, setMaxTokens] = useState(400);

  // image (vision)
  const [imageURL, setImageURL] = useState(null);      // dataURL
  const [imageName, setImageName] = useState("");

  // historique
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); }
    catch { return []; }
  });

  const {
    text, chars, cost, usage, loading, error, ask, cancel, prices
  } = useClaudeStream();

  // coût live estimé pendant le stream : input + output partiel
  const estLiveCost = useMemo(() => {
    if (!prices) return null;
    // on ne connaît pas les tokens d'entrée côté front -> approx depuis longueur du prompt
    const inTok = charsToTokens(msg.length);
    const outTok = charsToTokens(chars);
    const est = inTok * prices.token_in + outTok * prices.token_out;
    return est;
  }, [chars, msg, prices]);


  // gestion sectioon ouverture / fermeture section Stream
  useEffect(() => { localStorage.setItem(LS_OPEN, open ? "1" : "0"); }, [open]);

  // persiste l’historique (uniques, plus récent en premier)
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  }, [history]);

  if (!open) {
    return (
      <div className="stream-open-row">
        <button
          className="btn-ghost btn-neon stream-open-btn"
          onClick={() => setOpen(true)}
        >
          ✨ Ouvrir le test Claude (stream)
        </button>
      </div>
    );
  }

  const onLaunch = () => {
    if (!msg.trim() && !imageURL) return;
    ask({ message: msg, max_tokens: maxTokens, model, image: imageURL });
    // push dans l’historique (évite doublons successifs)
    setHistory((h) => {
      const clean = h.filter((x) => x !== msg.trim());
      return [msg.trim(), ...clean].slice(0, MAX_HISTORY);
    });
  };

  const onPickHistory = (p) => { setMsg(p); };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageName(f.name);
    const reader = new FileReader();
    reader.onload = () => setImageURL(reader.result); // dataURL
    reader.readAsDataURL(f);
  };

  const clearImage = () => { setImageURL(null); setImageName(""); };

  const pct = Math.min(100, Math.round((chars / 1200) * 100)); // heuristique barre

  return (
    <div className="claude-stream-box">
      {/* Header */}
      <div className="stream-header">
        <h3 className="stream-title">TEST CLAUDE <span>(STREAM)</span></h3>
        <div className="header-actions">
          <button className="btn-ghost" onClick={() => setMsg("")} title="Vider l'entrée">
            ⟲ <span className="txt">Reset</span>
          </button>
          <button className="btn-ghost danger" onClick={() => setOpen(false)} title="Fermer">
            ✕ <span className="txt">Fermer</span>
          </button>
        </div>
      </div>

      {/* Options: modèle + tokens + upload image */}
      <div className="stream-options">
        <label className="opt">
          <span>Modèle</span>
          <select value={model} onChange={(e) => setModel(e.target.value)}>
            {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>

        <label className="opt opt-range">
          <span>max_tokens: <b>{maxTokens}</b></span>
          <input
            type="range"
            min={50} max={2000} step={50}
            value={maxTokens}
            onChange={(e) => setMaxTokens(Number(e.target.value))}
          />
        </label>

        <label className="opt opt-file">
          <span>Image</span>
          <input type="file" accept="image/*" onChange={onPickImage} />
        </label>

        {imageURL && (
          <div className="image-chip">
            <img src={imageURL} alt="preview" />
            <span className="name" title={imageName}>{imageName}</span>
            <button className="btn-ghost danger sm" onClick={clearImage} title="Retirer">✕</button>
          </div>
        )}
      </div>

      {/* Row: prompt + bouton */}
      <div className="stream-row">
        <input
          className="prompt"
          type="text"
          placeholder="Tape ton prompt…"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          disabled={loading}
        />
        {!loading ? (
          <button className="btn-primary" onClick={onLaunch} disabled={!msg.trim() && !imageURL}>
            ⚡ Lancer (stream)
          </button>
        ) : (
          <button className="btn-primary warning" onClick={cancel}>⏹ Stop</button>
        )}
      </div>

      {/* Toolbar: progression + coût live/final + tokens */}
      <div className="stream-toolbar">
        <div className="progress" style={{ "--w": `${pct}%` }} />
        <span className="pill">{loading ? "Génération…" : "Prêt"}</span>
        {prices && (
          <span className="pill">
            Coût {loading ? "estimé" : "final"} :{" "}
            <b>${(loading ? estLiveCost ?? 0 : cost ?? 0).toFixed(6)}</b>
          </span>
        )}
        {usage && (
          <span className="pill">Tokens: in <b>{usage.input_tokens}</b> / out <b>{usage.output_tokens}</b></span>
        )}
      </div>

      {/* Sortie */}
      <div className="stream-output">
        <pre>{text || "—"}</pre>
      </div>

      {/* Historique */}
      {history.length > 0 && (
        <div className="history-row">
          <span className="h-label">Historique :</span>
          <div className="chips">
            {history.map((p, i) => (
              <button key={i} className="chip" onClick={() => onPickHistory(p)} title={p}>{p}</button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error-line">Erreur: {String(error)}</div>}
    </div>
  );
}
